import { useCallback, useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";

type LimitFields = {
  products: string;
  ordersPerMonth: string;
  locations: string;
  categories: string;
  brands: string;
  b2bUsers: string;
  images: string;
  emailsPerDay: string;
  pushPerDay: string;
  mcpCreditsMonth: string;
  mcpMaxUsers: string;
};

const EMPTY: LimitFields = {
  products: "",
  ordersPerMonth: "",
  locations: "",
  categories: "",
  brands: "",
  b2bUsers: "",
  images: "",
  emailsPerDay: "",
  pushPerDay: "",
  mcpCreditsMonth: "",
  mcpMaxUsers: "",
};

function numOrNull(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function fieldsFromOverride(raw: Record<string, unknown> | null | undefined): LimitFields {
  if (!raw || !Object.keys(raw).length) return { ...EMPTY };
  const n = (k: string, alt?: string) => {
    const v = raw[k] ?? (alt ? raw[alt] : undefined);
    return v === null || v === undefined ? "" : String(v);
  };
  return {
    products: n("products"),
    ordersPerMonth: n("ordersPerMonth", "orders_month"),
    locations: n("locations"),
    categories: n("categories"),
    brands: n("brands"),
    b2bUsers: n("b2bUsers", "b2b_users"),
    images: n("images"),
    emailsPerDay: n("emailsPerDay", "emails_per_day"),
    pushPerDay: n("pushPerDay", "push_per_day"),
    mcpCreditsMonth: n("mcpCreditsMonth", "mcp_credits_month"),
    mcpMaxUsers: n("mcpMaxUsers", "mcp_max_users"),
  };
}

function overrideFromFields(f: LimitFields): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const set = (key: string, val: string) => {
    if (val.trim() === "") return;
    const n = numOrNull(val);
    if (n !== null) out[key] = n;
  };
  set("products", f.products);
  set("ordersPerMonth", f.ordersPerMonth);
  set("locations", f.locations);
  set("categories", f.categories);
  set("brands", f.brands);
  set("b2bUsers", f.b2bUsers);
  set("images", f.images);
  set("emailsPerDay", f.emailsPerDay);
  set("pushPerDay", f.pushPerDay);
  set("mcpCreditsMonth", f.mcpCreditsMonth);
  set("mcpMaxUsers", f.mcpMaxUsers);
  return out;
}

export type TenantLimitsOverrideDialogProps = {
  tenantId: string | null;
  tenantName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export function TenantLimitsOverrideDialog({
  tenantId,
  tenantName,
  open,
  onOpenChange,
  onSaved,
}: TenantLimitsOverrideDialogProps) {
  const [fields, setFields] = useState<LimitFields>({ ...EMPTY });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const tenants = await apiGetJson<Array<{ id: string; limitsOverride?: Record<string, unknown> | null }>>(
        "/api/admin/tenants",
      );
      const t = tenants.find((x) => x.id === tenantId);
      setFields(fieldsFromOverride(t?.limitsOverride ?? null));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar límites");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) void load();
    if (!open) setFields({ ...EMPTY });
  }, [open, tenantId, load]);

  const save = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      const payload = overrideFromFields(fields);
      await apiSendJson("/api/admin/tenants", "PATCH", {
        tenantId,
        limitsOverride: Object.keys(payload).length ? payload : null,
      });
      toast.success("Override de límites guardado");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      await apiSendJson("/api/admin/tenants", "PATCH", { tenantId, limitsOverride: null });
      setFields({ ...EMPTY });
      toast.success("Override eliminado — aplica solo el plan");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, key: keyof LimitFields) => (
    <label className="space-y-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <Input
        type="number"
        className="h-8 bg-secondary/50 text-sm"
        placeholder="Plan"
        value={fields[key]}
        onChange={(e) => setFields((prev) => ({ ...prev, [key]: e.target.value }))}
      />
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Límites exclusivos</DialogTitle>
          <DialogDescription>
            {tenantName ? (
              <>
                Workspace <strong>{tenantName}</strong>. Valores vacíos = hereda del plan. No cambia la suscripción.
              </>
            ) : (
              "Sobrescribe límites del plan sin cambiar la suscripción."
            )}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {field("Productos", "products")}
            {field("Órdenes/mes", "ordersPerMonth")}
            {field("Sucursales", "locations")}
            {field("Categorías", "categories")}
            {field("Marcas", "brands")}
            {field("Usuarios B2B", "b2bUsers")}
            {field("Imágenes", "images")}
            {field("Emails/día", "emailsPerDay")}
            {field("Push/día", "pushPerDay")}
            {field("IA conectada: créditos/mes", "mcpCreditsMonth")}
            {field("IA conectada: usuarios", "mcpMaxUsers")}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={() => void clearAll()} disabled={saving || loading}>
            Quitar override
          </Button>
          <Button type="button" onClick={() => void save()} disabled={saving || loading}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
